// import React, { useEffect, useState } from 'react';
// import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
// import { TemplateActions } from '../../../Services/_actions/Template/template.actions.js';
// import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';

// interface TemplateListDropdownProps {
//     organisationId?: string;
//     botNumber?: string;
//     onChange: (value: any) => void;
//     value: any;
//     error?: boolean;
//     errorMessage?: string;
//     disabled?: boolean;
//     label?: string;
//     sx?: any;
// }

// const TemplateListDropdown: React.FC<TemplateListDropdownProps> = ({
//     organisationId,
//     botNumber,
//     onChange,
//     value,
//     error = false,
//     errorMessage = '',
//     disabled = false,
//     label = 'Select Template',
//     sx = {}
// }) => {
//     const { showSnackBar } = useSnackBar();
//     const templateActions = TemplateActions();
//     const [templateList, setTemplateList] = useState<any[]>([]);
//     const [loading, setLoading] = useState<boolean>(false);

//     useEffect(() => {
//         const fetchTemplates = async () => {
//             if (organisationId && botNumber) {
//                 setLoading(true);
//                 try {
//                     const res = await templateActions.getAllTemplates({
//                         organisation_id: organisationId,
//                         bot_number: botNumber,
//                     });

//                     if (res?.status === 200) {
//                         const data = res.data.map((tpl: any) => ({
//                             ...tpl,
//                             label: tpl.name,
//                             value: tpl.name, 
//                         }));

//                         setTemplateList(data);
//                         if (data?.length === 1) onChange(data[0]);
//                         else if (data.length === 0) showSnackBar('No templates found.', 'warning');
//                     } else {
//                         showSnackBar('Failed to fetch templates', 'error');
//                     }
//                 } catch (err) {
//                     showSnackBar(err?.toString() || 'Error fetching templates.', 'error');
//                 } finally {
//                     setLoading(false);
//                 }
//             } else {
//                 setTemplateList([]);
//             }
//         };

//         fetchTemplates();
//     }, [organisationId, botNumber]);

//     return (
//         <SearchableDropdown
//             label={label}
//             options={templateList}
//             value={value}
//             onChange={onChange}
//             error={error}
//             errorMessage={errorMessage}
//             disabled={disabled || templateList.length === 1}
//             loading={loading}
//             sx={sx}
//         />
//     );
// };

// export default TemplateListDropdown;
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { fetchTemplateListSelector } from '../../../Services/_states/Template/templateSelector.ts';
import { templateTableStateAtom } from '../../../Services/_states/index.js';
import { generateFilterKey } from '../../../utils/helper.jsx';
import useDebounce from '../Delay/index.tsx';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';

interface TemplateDropdownProps {
  value: any;
  onChange: (value: any) => void;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  disabled?: boolean;
  sx?: any;
  dropdownFilters?: any;
  //   organisation?: any; // Optional filtering by org
}

const TemplateDropdown: React.FC<TemplateDropdownProps> = ({
  value,
  onChange,
  error = false,
  errorMessage = '',
  label = 'Select Template',
  disabled = false,
  sx = { mb: 1, mt: 1 },
  dropdownFilters = null
  //   organisation,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [tableState, setTableState] = useRecoilState(templateTableStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchTemplateListSelector);
  const [filteredTemplates, setFilteredTemplates] = useState < any[] > ([]);

  const filterKey = generateFilterKey(tableState.filters);

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  useEffect(() => {
    setTableState((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        search: debouncedSearch ?? "",
        page: 0,
        organisation: dropdownFilters?.organisationId ?? "",
        botNumber: dropdownFilters?.botNumber ?? "",
        status: "APPROVED",
        // organisation: organisation?.organisation_id || '',
      },
    }));
  }, [debouncedSearch, dropdownFilters]);

  useEffect(() => {
    const allValues = Object.values(tableState.cache).flat();
    const uniqueTemplates = allValues.reduce((acc: any[], curr: any) => {
      if (!acc.some(item => item.name === curr.name && item.bot_number === curr.bot_number)) acc.push(curr);
      return acc;
    }, []);
    setFilteredTemplates(uniqueTemplates);
  }, [tableState.cache]);

  const loadMoreTemplates = () => {
    const total = tableState.totalCountCache[filterKey] || 0;
    const currentCount = Object.values(tableState.cache).flat().length;

    if (total > currentCount) {
      setTableState((prev) => ({
        ...prev,
        filters: { ...prev.filters, page: prev.filters.page + 1 },
      }));
    }
  };

  return (
    <SearchableDropdown
      options={filteredTemplates || []}
      label={label}
      value={value}
      onChange={onChange}
      labelKey="name"
      valueKey="name" // or bot_number if required to make unique
      onSearch={setSearchQuery}
      searchValue={searchQuery}
      fetchMoreOptions={loadMoreTemplates}
      loading={fetchedData.state === 'loading'}
      error={error}
      errorMessage={errorMessage}
      disabled={disabled}
      sx={sx}
      getOptionLabel={(option) => `${option?.name}`} // optional format
    />
  );
};

export default TemplateDropdown;
